import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';
import ConfigBusiness from '../config/ConfigBusiness.js';

var FCM_TOKEN_STORAGE_KEY = 'fcm_token';

function getOrInitApp() {
    var apps = getApps();
    return apps.length > 0 ? apps[0] : initializeApp(ConfigBusiness.get('firebase'));
}

function getApiBase() {
    try {
        var config = JSON.parse(localStorage.getItem('config')) || {};
        return config.apiUrlBase || '';
    } catch (e) {
        return '';
    }
}

function postToken(fcmToken) {
    var authToken = localStorage.getItem('token') || '';
    if (!authToken) {
        return Promise.resolve();
    }
    return fetch(getApiBase() + '/notificaciones/fcm-token', {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization-Token': authToken,
        },
        body: JSON.stringify({ token: fcmToken, dispositivo: 'web' }),
    }).then(function() {
        localStorage.setItem(FCM_TOKEN_STORAGE_KEY, fcmToken);
    }).catch(function(err) {
        console.error('[FcmTokenService] Error al registrar token:', err);
    });
}

function deleteToken(fcmToken) {
    // Limpiar localStorage siempre, independientemente de si el backend responde bien.
    // Si no se limpia aquí, el próximo login ve el token viejo y no re-registra.
    localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);

    var authToken = localStorage.getItem('token') || '';
    if (!fcmToken || !authToken) {
        return Promise.resolve();
    }
    return fetch(getApiBase() + '/notificaciones/fcm-token', {
        method: 'DELETE',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization-Token': authToken,
        },
        body: JSON.stringify({ token: fcmToken }),
    }).catch(function(err) {
        console.error('[FcmTokenService] Error al eliminar token:', err);
    });
}

var FcmTokenService = {
    /**
     * Requests push permission (if not already granted/denied) and registers
     * the FCM token with the backend. Safe to call on every app load — it
     * short-circuits if permission was already denied or a token is already stored.
     * Returns a promise that resolves to true if the token was registered.
     */
    solicitarPermisoPush: function() {
        if (!('Notification' in window) || !('serviceWorker' in navigator)) {
            return Promise.resolve(false);
        }

        if (Notification.permission === 'denied') {
            return Promise.resolve(false);
        }

        return Notification.requestPermission().then(function(permission) {
            if (permission !== 'granted') {
                return false;
            }

            var app = getOrInitApp();
            var messaging = getMessaging(app);

            // Desregistrar el SW estático anterior si todavía está activo.
            // Necesario para la transición de /firebase-messaging-sw.js al endpoint dinámico.
            var desregistrarViejo = navigator.serviceWorker.getRegistrations().then(function(registrations) {
                registrations.forEach(function(reg) {
                    if (reg.active && reg.active.scriptURL.indexOf('firebase-messaging-sw.js') !== -1) {
                        reg.unregister();
                    }
                });
            });

            return desregistrarViejo
                .then(function() {
                    return navigator.serviceWorker.register(getApiBase() + '/public/push/firebase-sw.js');
                })
                .then(function(registration) {
                    return getToken(messaging, {
                        vapidKey: ConfigBusiness.get('firebase.vapidKey'),
                        serviceWorkerRegistration: registration,
                    });
                })
                .then(function(currentToken) {
                    if (!currentToken) {
                        return false;
                    }
                    // Si el token no cambió respecto al guardado, no hace falta re-registrar.
                    if (localStorage.getItem(FCM_TOKEN_STORAGE_KEY) === currentToken) {
                        return true;
                    }
                    // Token nuevo o rotado — registrar en el backend.
                    return postToken(currentToken).then(function() { return true; });
                });
        }).catch(function(err) {
            console.error('[FcmTokenService] Error al solicitar permiso push:', err);
            return false;
        });
    },

    /**
     * Deletes the current device's FCM token from the backend.
     * Call this before clearing localStorage on logout.
     */
    eliminarTokenActual: function() {
        var fcmToken = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
        return deleteToken(fcmToken);
    },
};

export default FcmTokenService;
